import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import UnsavedChangesDialog from '../components/shared/UnsavedChangesDialog';
import UnsavedChangesBanner from '../components/shared/UnsavedChangesBanner';

/**
 * Hook for unsaved changes protection.
 *
 * Page mode (default): blocks route navigation + browser close, shows save banner.
 *   const { UnsavedChangesDialog, SaveBanner } = useUnsavedChanges({ hasChanges, onSave });
 *
 * Dialog mode: guards dialog close (backdrop click, Escape).
 *   const { guardedOnClose, UnsavedChangesDialog } = useUnsavedChanges({
 *     hasChanges, onSave, mode: 'dialog', onClose,
 *   });
 *
 * `onSave` signals failure by resolving to exactly `false` (any other value, including
 * undefined, counts as success). Rejecting also works, but prefer `return false`: the
 * same save function is usually wired to a button's onClick, where a rejected promise
 * nobody awaits becomes an unhandled rejection and a Sentry error on every ordinary
 * validation 400 (VERSTAANI-FRONTEND-G).
 */
export function useUnsavedChanges({
  hasChanges,
  onSave,
  enabled = true,
  mode = 'page',
  onClose, // required for dialog mode
}) {
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const onSaveRef = useRef(onSave);
  const onCloseRef = useRef(onClose);

  // Keep refs current to avoid stale closures
  useEffect(() => { onSaveRef.current = onSave; }, [onSave]);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  // --- Page mode: route blocking ---
  const shouldBlock = mode === 'page' && hasChanges && enabled;
  const blocker = useBlocker(shouldBlock);

  // --- Page mode: beforeunload ---
  useEffect(() => {
    if (!shouldBlock) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [shouldBlock]);

  // --- Page mode: blocker dialog handlers ---
  const handleBlockerSave = useCallback(async () => {
    setSaving(true);
    try {
      const saved = await onSaveRef.current();
      if (saved !== false && blocker.state === 'blocked') {
        blocker.proceed();
      }
    } catch (err) {
      // Save failed — stay on page
    } finally {
      setSaving(false);
    }
  }, [blocker]);

  const handleBlockerDiscard = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [blocker]);

  const handleBlockerCancel = useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  // --- Dialog mode: guarded close ---
  const guardedOnClose = useCallback((event, reason) => {
    if (hasChanges) {
      setShowDialog(true);
    } else {
      onCloseRef.current?.(event, reason);
    }
  }, [hasChanges]);

  const handleDialogSave = useCallback(async () => {
    setSaving(true);
    try {
      const saved = await onSaveRef.current();
      if (saved === false) {
        // Save failed — close the unsaved changes dialog so user can see
        // validation errors in the main form, but do NOT close the main dialog
        setShowDialog(false);
        return;
      }
      // Save succeeded — the save function itself typically calls onClose,
      // but call it here too in case it doesn't (idempotent)
      setShowDialog(false);
      onCloseRef.current?.();
    } catch (err) {
      setShowDialog(false);
    } finally {
      setSaving(false);
    }
  }, []);

  const handleDialogDiscard = useCallback(() => {
    setShowDialog(false);
    onCloseRef.current?.();
  }, []);

  const handleDialogCancel = useCallback(() => {
    setShowDialog(false);
  }, []);

  // --- Banner save handler (page mode) ---
  const [bannerSaving, setBannerSaving] = useState(false);
  const handleBannerSave = useCallback(async () => {
    setBannerSaving(true);
    try {
      await onSaveRef.current();
    } catch (err) {
      // Save failed — banner stays visible
    } finally {
      setBannerSaving(false);
    }
  }, []);

  // --- Render elements ---
  if (mode === 'dialog') {
    return {
      guardedOnClose,
      UnsavedChangesDialog: (
        <UnsavedChangesDialog
          open={showDialog}
          onSave={handleDialogSave}
          onDiscard={handleDialogDiscard}
          onCancel={handleDialogCancel}
          saving={saving}
        />
      ),
    };
  }

  // Page mode
  return {
    UnsavedChangesDialog: (
      <UnsavedChangesDialog
        open={blocker.state === 'blocked'}
        onSave={handleBlockerSave}
        onDiscard={handleBlockerDiscard}
        onCancel={handleBlockerCancel}
        saving={saving}
      />
    ),
    SaveBanner: (
      <UnsavedChangesBanner
        visible={hasChanges && enabled}
        onSave={handleBannerSave}
        saving={bannerSaving}
      />
    ),
  };
}
